import { useAuth } from '../../hooks/useAuth'
import { MerchantLayout } from '../../components/layouts/Layout'
import { Card, Button } from '../../components/common/UI'
import { TrendingUp, Eye, MousePointer, Target, Plus, Building, Coins } from 'lucide-react'
import { formatters } from '../../utils/formatting'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { advertisementService, businessService, merchantService } from '../../services/api'

export default function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [advertisements, setAdvertisements] = useState<any[]>([])
  const [businesses, setBusinesses] = useState<any[]>([])
  const [creditBalance, setCreditBalance] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      if (!user?.merchantId) return
      
      try {
        const [ads, biz, merchant] = await Promise.all([
          advertisementService.listByMerchant(user.merchantId),
          businessService.listByMerchant(user.merchantId),
          merchantService.get(user.merchantId)
        ])
        
        setAdvertisements(ads)
        setBusinesses(biz)
        setCreditBalance(merchant?.creditBalance || 0)
      } catch (error) {
        console.error('Failed to load dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [user?.merchantId])

  const metrics = advertisements.reduce((acc, ad: any) => ({
    impressions: acc.impressions + (ad.impressionCount || 0),
    clicks: acc.clicks + (ad.clickCount || 0),
    ctr: 0
  }), { impressions: 0, clicks: 0, ctr: 0 })

  metrics.ctr = metrics.impressions > 0 ? (metrics.clicks / metrics.impressions) * 100 : 0

  if (loading) {
    return (
      <MerchantLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p>Loading...</p>
        </div>
      </MerchantLayout>
    )
  }

  return (
    <MerchantLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Dashboard</h1>
            <p className="text-gray-600">Welcome back, {user?.fullName}</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" onClick={() => navigate('/merchant/businesses')} icon={Building}>
              Add Business
            </Button>
            <Button onClick={() => navigate('/merchant/advertisements')} icon={Plus}>
              Create Ad
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Credit Balance</p>
                <p className="text-2xl font-bold">{formatters.number(creditBalance)}</p>
              </div>
              <Coins className="w-8 h-8 text-yellow-600" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Impressions</p>
                <p className="text-2xl font-bold">{formatters.number(metrics.impressions)}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-600" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Clicks</p>
                <p className="text-2xl font-bold">{formatters.number(metrics.clicks)}</p>
              </div>
              <MousePointer className="w-8 h-8 text-green-600" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">CTR</p>
                <p className="text-2xl font-bold">{metrics.ctr.toFixed(2)}%</p>
              </div>
              <Target className="w-8 h-8 text-purple-600" />
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Your Businesses</h3>
              <Button size="sm" variant="outline" onClick={() => navigate('/merchant/businesses')}>
                View All
              </Button>
            </div>
            <div className="space-y-3">
              {businesses.slice(0, 5).map((business: any) => (
                <div key={business.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{business.businessName}</p>
                    <p className="text-sm text-gray-600">{business.businessType}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    business.verificationStatus === 'VERIFIED' ? 'bg-green-100 text-green-800' :
                    business.verificationStatus === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {business.verificationStatus}
                  </span>
                </div>
              ))}
              {businesses.length === 0 && (
                <p className="text-center text-gray-500 py-4">No businesses yet</p>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Active Ads</h3>
              <Button size="sm" variant="outline" onClick={() => navigate('/merchant/advertisements')}>
                View All
              </Button>
            </div>
            <div className="space-y-3">
              {advertisements.filter((ad: any) => ad.isActive).slice(0, 5).map((ad: any) => (
                <div key={ad.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium">{ad.adTitle}</p>
                    <p className="text-sm text-gray-600">
                      {formatters.number(ad.impressionCount || 0)} impressions
                    </p>
                  </div>
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
              ))}
              {advertisements.filter((ad: any) => ad.isActive).length === 0 && (
                <p className="text-center text-gray-500 py-4">No active ads</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </MerchantLayout>
  )
}